import { useMemo, useState } from 'react';
import { ContentWithLegendProps } from '@/components';
import { TimeSeriesDataset } from '@/core';
import { TimeSeriesChartProps } from '../time-series-chart-model';

interface TimeSeriesFilterSeriesFnOpts extends Pick<TimeSeriesChartProps, 'enableLabelsToColumns'> {}

/**
 * Returns true if the specified content matches the specified filter.
 */
function contentMatchesQuickFilter(content: string | number | undefined, quickFilter: string) {
  if (!content) {
    return false;
  }

  // This functionality is currently very simplistic as an MVP. It will likely grow to include more
  // complex functionality in the future. When extending this feature, be sure to evaluate the performance
  // implications for large scales of data.
  return `${content}`.toLowerCase().includes(quickFilter);
}

/**
 * Function for determining if a series should be filtered out based on the quick filter value.
 */
const filterSeriesFn = (
  /**
   * Dataset is source of truth for series name and labels.
   */
  timeSeries: TimeSeriesDataset,
  /**
   * String used to filter the results.
   */
  quickFilter: string,
  { enableLabelsToColumns }: TimeSeriesFilterSeriesFnOpts = {}
) => {
  const adjustedQuickFilter = quickFilter.trim().toLowerCase();

  if (!adjustedQuickFilter) {
    // Always show series if filter is empty.
    return true;
  }

  const nameMatches = contentMatchesQuickFilter(timeSeries.name, adjustedQuickFilter);
  const formattedNameMatches = contentMatchesQuickFilter(timeSeries.formattedName, adjustedQuickFilter);

  if (nameMatches || formattedNameMatches) {
    return true;
  }

  // When labels are shown, also check their values.
  if (enableLabelsToColumns && timeSeries.labels) {
    for (const labelKey in timeSeries.labels) {
      // Return on first match.
      if (contentMatchesQuickFilter(timeSeries.labels[labelKey], adjustedQuickFilter)) {
        return true;
      }
    }
  }

  // No matches found.
  return false;
};

export interface UseTimeSeriesQuickFilterProps extends Pick<TimeSeriesChartProps, 'showQuickFilter'> {}

export interface UseTimeSeriesQuickFilterResponse extends Pick<ContentWithLegendProps, 'quickFilterProps'> {
  quickFilter: string;
  filterSeriesFn: typeof filterSeriesFn;
}

/**
 * Hook to help with use of the "quick filter" feature in the time series chart.
 */
export const useTimeSeriesQuickFilter = ({
  showQuickFilter,
}: UseTimeSeriesQuickFilterProps): UseTimeSeriesQuickFilterResponse => {
  const [quickFilter, setQuickFilter] = useState<string>('');

  const quickFilterProps: ContentWithLegendProps['quickFilterProps'] = useMemo(() => {
    return {
      show: showQuickFilter,
      onChange: setQuickFilter,
    };
  }, [showQuickFilter]);

  return {
    quickFilterProps,
    quickFilter,
    filterSeriesFn,
  };
};
